/* ============================== Config ============================== */

const API = {
  tracking: 'https://public.jongstratransport.nl/order_tracking',
  delivery: 'https://api.delivery.nu/jongstra/jobs/reference',
  // App proxy: same-origin, so no token in the theme. Shopify signs the request,
  // the app verifies the e-mail against the order.
  order: '/apps/order-proxy/order',
};

const REQUEST_TIMEOUT = 10000;

// For order/transport calls only: keeps personal data out of caches. Never pass
// this to a storefront URL — the Accept header makes Shopify ignore ?view=.
const PRIVATE = { cache: 'no-store', headers: { Accept: 'application/json' } };

const STORE_ROOT = window.Shopify?.routes?.root || '/';

const STEPS = ['received', 'postponed', 'delayed', 'to_warehouse', 'at_warehouse', 'carrier', 'delivered'];

// The three options the shop sells, as the customer picked them at checkout.
const SHIPPING = {
  standard: 'Standaard levering - tot aan de eerste drempel',
  montage: 'Bezorging + montage - in huis',
  postnl: 'PostNL verzending',
};

const TEXT = {
  pending: 'Wordt bepaald zodra je levering is ingepland',
  notfound: 'We konden geen bestelling vinden met dit ordernummer en e-mailadres. Controleer je gegevens.',
  error: 'Er ging iets mis bij het ophalen van je bestelling. Probeer het later opnieuw.',
  empty: 'Vul je ordernummer en e-mailadres in.',
};

// Customers often type just the digits ("12345", "#12345"); the APIs only know the
// full reference ("MHO12345"). Other input is left as is.
const normalizeOrder = (value) => {
  const order = value.trim();
  const digits = order.match(/^#?\s*(?:MHO[\s-]*)?(\d+)$/i)?.[1];
  return digits ? `MHO${digits}` : order;
};

// A data-text-* attribute on the section overrides the default above.
const textOf = (root, key) => root.dataset[`text${key[0].toUpperCase()}${key.slice(1)}`] || TEXT[key];

/* ================================ API ================================ */

// Every failure ends up as null, so log what went wrong — without the query
// string, which carries the customer's order number and e-mail.
const fetchJson = async (url, init) => {
  const where = String(url).split('?')[0];
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(REQUEST_TIMEOUT), ...init });
    if (res.ok) return await res.json();
    console.warn(`[order-tracking] ${res.status} ${res.statusText} — ${where}`);
  } catch (error) {
    console.warn(`[order-tracking] ${where}`, error);
  }
  return null;
};

const getOrder = async (order, email) => {
  const url = new URL(API.tracking);
  url.searchParams.set('reference', order);
  url.searchParams.set('email', email);
  const data = await fetchJson(url, PRIVATE);
  return (Array.isArray(data) ? data[0] : data) ?? null;
};

// include_oms adds external parcels (PostNL), but only when search matches the
// delivery zip/email/phone.
const getDeliveryEntries = async (order, zip) => {
  const url = new URL(`${API.delivery}/${encodeURIComponent(order)}`);
  if (zip) url.searchParams.set('search', zip);
  url.searchParams.set('include_oms', 'true');
  const data = await fetchJson(url, PRIVATE);
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

// Round to cents so summed floats don't surface as 4.999999999.
const roundCents = (value) => (value == null ? null : Math.round(value * 100) / 100);

// Shopify CDN images resize by query param; 104 = 2x the 52px row thumbnail.
const thumb = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=104` : '');

const amountOf = (bag) => (bag?.amount == null ? null : roundCents(Number(bag.amount)));

// A cut on the line itself shows as a struck price on the row; an order-wide code
// shows once in the totals. discountedTotal covers only the first.
const proxyLine = (item) => {
  const quantity = Number(item.quantity) || 1;
  const before = amountOf(item.originalTotal);
  const onLine = amountOf(item.discountedTotal) ?? before ?? 0;

  return {
    sku: item.sku || '',
    title: item.title || '',
    variant: item.variantTitle || '',
    handle: item.productHandle || '',
    imageUrl: thumb(item.image?.url),
    imageAlt: item.image?.altText || '',
    unitPriceBeforeDiscount: before == null ? null : roundCents(before / quantity),
    unitPrice: roundCents(onLine / quantity),
    // A gift line, or one discounted down to nothing.
    isFree: onLine === 0,
    isDiscounted: onLine < (before ?? 0),
  };
};

// The current* totals follow order edits and refunds; grandTotal does not.
const fromProxy = (order) => {
  // A removed or refunded line stays in the order with currentQuantity 0.
  const items = (order.lineItems ?? []).filter((item) => (item.currentQuantity ?? 1) > 0);
  const totals = order.totals ?? {};
  const subtotal = amountOf(totals.currentSubtotal);
  const discount = amountOf(totals.currentDiscounts) ?? 0;
  // Codes only: a manual discount's title is an internal note ("verkeerde prijs
  // in showroom"), not for a customer's eyes.
  const codes = items.flatMap((item) => (item.discounts ?? []).map((d) => d.code));

  return {
    // The only source that tells PostNL apart from own delivery.
    shippingMethods: (order.shippingLines ?? []).map((line) => line.title || ''),
    lines: items.map(proxyLine),
    totals: {
      // currentSubtotal is after discount; the row shows the amount before it.
      subtotal: subtotal == null ? null : roundCents(subtotal + discount),
      discount,
      shipping: amountOf(totals.currentShipping),
      total: amountOf(totals.currentGrandTotal),
      codes: [...new Set(codes.filter(Boolean))],
    },
  };
};

// Unknown order, mismatched e-mail and an unreachable app all give null, so the
// page still renders on transport data alone.
const getShopOrder = async (order, email) => {
  const url = new URL(API.order, location.origin);
  url.searchParams.set('order_number', order);
  url.searchParams.set('email', email);
  const data = await fetchJson(url, PRIVATE);
  return data?.lineItems ? fromProxy(data) : null;
};

const splitEntries = (entries) => ({
  job: entries.find((entry) => entry.tracking_source !== 'oms') || null,
  parcels: entries.filter((entry) => entry.tracking_source === 'oms'),
});

// Package weight/dimensions live in metafields, so they are the one thing the
// order can't tell us. Cached per handle: one request per product, not per line.
const specsCache = new Map();

const getSpecs = (handle) => {
  if (!handle) return Promise.resolve(null);
  if (!specsCache.has(handle)) {
    const url = new URL(`${STORE_ROOT}products/${encodeURIComponent(handle)}?view=pakket`, location.origin);
    specsCache.set(handle, fetchJson(url).then((data) => (data ? packageSpecs(data) : null)));
  }
  return specsCache.get(handle);
};

// A SKU can appear on several lines, so each lookup takes the next unused one.
const linesBySku = (shopOrder) => {
  const map = new Map();
  for (const line of shopOrder?.lines ?? []) {
    if (!line.sku) continue;
    if (!map.has(line.sku)) map.set(line.sku, []);
    map.get(line.sku).push(line);
  }
  return map;
};

const takeLine = (map, sku) => map.get(sku)?.shift() ?? null;

/* ============================= Formatting ============================= */

const formatDate = (iso, opts) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('nl-NL', opts).format(date);
};

const shortDate = (iso) => formatDate(iso, { day: '2-digit', month: '2-digit', year: 'numeric' });

const countryName = (code) => {
  if (!code) return '';
  try {
    return new Intl.DisplayNames(['nl'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
};

// € 279,- / € 303,95 / € 1.343,45 / € 0,00
const money = (value) => {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return '';
  if (n === 0) return '€ 0,00';
  const whole = Math.trunc(n);
  const cents = Math.round((n - whole) * 100);
  const grouped = new Intl.NumberFormat('nl-NL').format(whole);
  return cents === 0 ? `€ ${grouped},-` : `€ ${grouped},${String(cents).padStart(2, '0')}`;
};

// Accepts a number, "15,5 kg" or { value }.
const metaNumber = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'object') return Number(value.value) || null;
  const match = String(value).replace(',', '.').match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

// breedte x diepte x hoogte
const formatDimensions = (specs) => {
  const parts = [specs?.width, specs?.depth, specs?.height].map(metaNumber).filter((n) => n != null);
  return parts.length ? `${parts.join(' x ')} cm` : '';
};

// Falls back to the product's own specs when the package metafields are empty.
const packageSpecs = (data) => {
  const packages = (data.packages ?? []).filter((box) => box.weight || box.dimensions);
  if (!packages.length) {
    const weight = metaNumber(data.weight);
    return { weight, boxes: [{ weight, dimensions: formatDimensions(data) }] };
  }

  const boxes = packages.map((box) => ({ weight: metaNumber(box.weight), dimensions: box.dimensions || '' }));
  return { weight: boxes.reduce((total, box) => total + (box.weight ?? 0), 0) || null, boxes };
};

// Article label; the shop title beats the warehouse description from the API.
const articleName = (item, line) =>
  `${item.amount > 1 ? `${item.amount}× ` : ''}${line?.title || item.name || ''}`.trim();

const boxesOf = (specs) => (specs?.boxes ?? []).filter((box) => box.weight || box.dimensions);

const hasPackageData = (specs) => boxesOf(specs).length > 0;

const soloDetails = (item, specs) => {
  const boxes = boxesOf(specs);
  if (boxes.length !== 1) return articleDetails(item, specs);

  const [box] = boxes;
  const suffix = item.amount > 1 ? ' per doos' : '';
  return [
    box.weight ? `Gewicht: ${formatWeight(box.weight)}${suffix}` : '',
    box.dimensions ? `Afmetingen: ${box.dimensions}` : '',
  ].filter(Boolean);
};

const articleDetails = (item, specs) => {
  const boxes = boxesOf(specs);
  if (!boxes.length) return ['Onbekend'];

  // A single box shipped several times: say that the weight is per box.
  const suffix = boxes.length === 1 && item.amount > 1 ? ' per doos' : '';
  return boxes.map((box, i) => {
    const label = boxes.length > 1 ? `Doos ${i + 1}: ` : '';
    const weight = box.weight ? `${formatWeight(box.weight)}${suffix}` : '';
    return `${label}${[weight, box.dimensions].filter(Boolean).join(' · ')}`;
  });
};

const formatWeight = (kg) => `${String(Math.round(kg * 10) / 10).replace('.', ',')} kg`;

const addressLines = (place = {}, name) => [
  name ?? place.name,
  [place.street, place.street_number].filter(Boolean).join(' '),
  [formatZip(place.zip), place.city].filter(Boolean).join(' '),
  countryName(place.country),
];

// "1234ab" -> "1234 AB"; other formats stay as they are.
const formatZip = (zip) => {
  if (!zip) return '';
  const m = zip.replace(/\s+/g, '').match(/^(\d{4})([A-Za-z]{2})$/);
  return m ? `${m[1]} ${m[2].toUpperCase()}` : zip;
};

/* ============================ Order status ============================ */

// Rate names differ per order ("standaard bezorging", "standaard levering"), so
// they are mapped onto the three fixed options. Montage also comes from
// order_tracking, which knows it before a delivery job exists. An order that mixes
// a parcel with furniture counts as own delivery.
const deliveryOption = (tracking, shopOrder) => {
  const methods = shopOrder?.shippingMethods ?? [];
  if (tracking?.assembly_on_delivery || methods.some((m) => /montage/i.test(m))) return SHIPPING.montage;
  if (methods.length && methods.every((m) => /postnl/i.test(m))) return SHIPPING.postnl;
  return SHIPPING.standard;
};

const expectedWeek = (tracking) => {
  const weeks = (tracking?.items || [])
    .filter((i) => i.stock_status === 'wait' && i.stock_date)
    .map((i) => i.stock_date);
  return weeks.length ? Math.max(...weeks) : null;
};

const deliveryDate = (job) =>
  job?.tour?.date ||
  job?.tour_date ||
  (job?.proposals || []).find((p) => p.status !== 'rejected')?.proposal_date ||
  null;

const parseDate = (value) => {
  const [, day, month, year] = String(value ?? '').match(/^(\d{2})-(\d{2})-(\d{4})$/) ?? [];
  return day ? new Date(`${year}-${month}-${day}`) : null;
};

// Used when there is no delivery job: PostNL orders ship straight from the warehouse.
const latestItemDate = (tracking, field) =>
  (tracking?.items ?? [])
    .map((item) => item[field])
    .filter(parseDate)
    .sort((a, b) => parseDate(a) - parseDate(b))
    .pop() ?? '';

// Delivered means the tour ran, or the items carry a delivery date (parcel orders
// have no tour). The warehouse status is no proof: it reads "finished" as soon as
// the warehouse is done with the order, which can be the day it was placed.
const isDelivered = ({ tracking, job }) =>
  job?.status === 'completed' || Boolean(latestItemDate(tracking, 'delivery_date'));

const currentStep = (ctx) => {
  const items = ctx.tracking?.items || [];
  if (isDelivered(ctx)) return STEPS.length;
  if (ctx.job || items.some((i) => i.delivery_number)) return STEPS.indexOf('carrier');
  if (items.length && !items.some((i) => i.stock_status === 'wait')) return STEPS.indexOf('at_warehouse');
  if (items.some((i) => i.stock_status === 'wait')) return STEPS.indexOf('to_warehouse');
  return 0;
};

const rejectedProposals = (job) => (job?.proposals || []).filter((p) => p.status === 'rejected');

// On-request = customer asked to reschedule; otherwise the shop moved it (= delayed).
const isOnRequest = (p) => p.delivery_preference === 'new_delivery_proposal' || Boolean(p.notes);

const stepApplies = (key, { job }) => {
  if (key === 'postponed') return rejectedProposals(job).some(isOnRequest);
  if (key === 'delayed') return rejectedProposals(job).some((p) => !isOnRequest(p));
  return true;
};

const stepDetail = (key, { tracking, job }, done) => {
  const fallback = done ? 'Datum onbekend' : 'Datum volgt';
  switch (key) {
    case 'received':
      return tracking?.order_date || shortDate(job?.created_date) || fallback;
    case 'postponed':
      return shortDate(rejectedProposals(job).find(isOnRequest)?.proposal_date) || fallback;
    case 'delayed':
      return shortDate(rejectedProposals(job).find((p) => !isOnRequest(p))?.proposal_date) || fallback;
    case 'to_warehouse': {
      const week = expectedWeek(tracking);
      return week ? `Verwacht week ${week}` : '';
    }
    case 'at_warehouse':
      return done ? 'Op voorraad' : 'In afwachting';
    case 'carrier':
      return shortDate(job?.created_date) || latestItemDate(tracking, 'delivery_created_date') || fallback;
    case 'delivered': {
      const date = shortDate(deliveryDate(job)) || latestItemDate(tracking, 'delivery_date');
      if (!date) return fallback;
      return done ? date : `Verwacht ${date}`;
    }
    default:
      return '';
  }
};

/* ============================= DOM helpers ============================= */

const chevron = () => document.getElementById('order-tracking-chevron')?.content.cloneNode(true) ?? '';

const setText = (node, value) => {
  if (node) node.textContent = value ?? '';
};

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
};

/* =============================== Render =============================== */

// The amount actually paid, preceded by the struck original when discounted.
const priceNodes = (line) => {
  const nodes = [];
  if (line.isDiscounted && line.unitPriceBeforeDiscount > line.unitPrice) {
    nodes.push(el('s', 'order-tracking__item-price-was', money(line.unitPriceBeforeDiscount)));
  }
  // "Gratis" only for a line given away; a service line priced at zero shows its amount.
  const paid = line.isFree && line.isDiscounted ? 'Gratis' : money(line.unitPrice);
  nodes.push(el('span', 'order-tracking__item-price-now', paid));
  return nodes;
};

const isOffer = (line) => line.isFree || (line.isDiscounted && line.unitPriceBeforeDiscount > line.unitPrice);

const itemRow = (item, line) => {
  const figure = el('figure', 'order-tracking__item-media');
  // Matches the fixed 52px media box in order-tracking.css, so the row can't shift.
  const img = el('img', 'order-tracking__item-img');
  Object.assign(img, { loading: 'lazy', decoding: 'async', width: 52, height: 52 });
  if (line?.imageUrl) {
    img.src = line.imageUrl;
    img.alt = line.imageAlt || line.title || '';
  }
  figure.append(img);

  const price = el('div', 'order-tracking__item-price');
  if (line) {
    price.classList.toggle('order-tracking__item-price--sale', isOffer(line));
    price.replaceChildren(...priceNodes(line));
  }

  const info = el('div', 'order-tracking__item-info');
  info.append(
    el('div', 'order-tracking__item-name', articleName(item, line)),
    el('div', 'order-tracking__item-variant', line?.variant),
    price,
  );

  const row = el('div', 'order-tracking__item');
  row.append(figure, info);
  return row;
};

const fillCell = (cell, value) => {
  [value].flat().filter(Boolean).forEach((line, i) => {
    if (i) cell.append(el('br'));
    cell.append(line);
  });
  return cell;
};

// BEM classes off one base: __row, __label, __value. Either cell takes a string,
// a node, or an array for several lines.
const defList = (base, rows) => {
  const dl = el('dl', `order-tracking__${base}`);
  for (const [label, value, modifier = ''] of rows) {
    const row = el('div', `order-tracking__${base}-row${modifier}`);
    row.append(
      fillCell(el('dt', `order-tracking__${base}-label`), label),
      fillCell(el('dd', `order-tracking__${base}-value`), value),
    );
    dl.append(row);
  }
  return dl;
};

const totalsList = (totals) => {
  const codes = (totals.codes ?? []).join(', ');

  return defList('totals', [
    ['Subtotaal', money(totals.subtotal)],
    [
      ['Korting', codes && el('span', 'order-tracking__totals-code', codes)],
      totals.discount > 0 ? `- ${money(totals.discount)}` : '',
      ' order-tracking__totals-row--discount',
    ],
    ['Bezorgkosten', totals.shipping === 0 ? 'Gratis' : money(totals.shipping)],
    ['Totaal', money(totals.total), ' order-tracking__totals-row--total'],
  ].filter(([, value]) => value));
};

const metaList = (rows) => {
  const scroll = el('div', 'order-tracking__meta-scroll');
  scroll.append(defList('meta', rows.filter(([, value]) => value)));
  return scroll;
};

const paragraph = (lines, heading) => {
  const p = document.createElement('p');
  const rows = lines.filter(Boolean);
  if (heading) p.append(el('strong', '', heading));
  if (heading && rows.length) p.append(el('br'));
  rows.forEach((line, i) => {
    if (i) p.append(el('br'));
    p.append(line);
  });
  return p;
};

const link = (href, text) =>
  Object.assign(document.createElement('a'), { href, textContent: text, target: '_blank', rel: 'noopener' });

// Carrier URLs come from the transport API: anything but http(s) — javascript:
// above all — must never end up in an href.
const httpUrl = (value) => {
  try {
    const url = new URL(value, location.origin);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
  } catch {
    return '';
  }
};

// The cards are rendered by the separate "order-details" section, so they live
// outside <order-tracking>.
const fillCard = (key, ...nodes) => {
  document.querySelector(`[data-card="${key}"]`)?.replaceChildren(...nodes);
};

const traceBlock = (heading, links) =>
  paragraph(
    links.map(({ url, code }) => {
      const href = httpUrl(url);
      return href ? link(href, code || 'Volg je zending') : code || '-';
    }),
    heading,
  );

const CARRIERS = { postnl: 'PostNL', dhl: 'DHL', dpd: 'DPD', ups: 'UPS', gls: 'GLS' };

// Carrier and barcode for a parcel URL (Sendcloud query params or a carrier path).
const parcelInfo = (url) => {
  const { hostname, pathname, searchParams } = new URL(url, location.origin);
  const carrier = (searchParams.get('carrier') || hostname).toLowerCase();
  const segment = pathname.split('/').pop().split('-')[0];
  return {
    carrier: CARRIERS[Object.keys(CARRIERS).find((name) => carrier.includes(name))] ?? 'pakket',
    code: searchParams.get('code') || (/^[a-z0-9]{8,}$/i.test(segment) ? segment : ''),
  };
};

// Track & trace block per external parcel; a "-S1" reference is a follow-up shipment.
const parcelBlocks = (parcels) =>
  parcels
    .filter((parcel) => parcel.parcel_tracking_urls?.some(httpUrl))
    .map((parcel) => {
      const links = parcel.parcel_tracking_urls.filter(httpUrl).map((url) => ({ url, ...parcelInfo(url) }));
      const followUp = /-S\d+/.test(parcel.reference ?? '') ? ' (nazending)' : '';
      return traceBlock(`Track & trace ${links[0].carrier}${followUp}`, links);
    });

const renderTitle = (root, ctx) => {
  setText(root.querySelector('[data-field="delivery_label"]'), isDelivered(ctx) ? 'Bestelling geleverd:' : 'Verwacht bezorgmoment:');

  const date = deliveryDate(ctx.job) || parseDate(latestItemDate(ctx.tracking, 'delivery_date'));
  const eta = ctx.job?.scheduled_eta || ctx.job?.live_eta;
  const long = formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' });
  const value = date
    ? long.charAt(0).toUpperCase() + long.slice(1) + (eta ? ` rond ${eta} uur` : '')
    : textOf(root, 'pending');
  setText(root.querySelector('[data-field="expected_delivery"]'), value);
};

const renderTimeline = (root, ctx) => {
  const current = currentStep(ctx);
  STEPS.forEach((key, i) => {
    const step = root.querySelector(`[data-step="${key}"]`);
    if (!step) return;

    step.hidden = !stepApplies(key, ctx);
    if (step.hidden) return;

    const done = i < current;
    step.classList.toggle('order-tracking__timeline-step--done', done);
    step.classList.toggle('order-tracking__timeline-step--current', i === current);
    setText(step.querySelector('[data-step-detail]'), stepDetail(key, ctx, done));
  });

  const status = root.querySelector('[data-field="timeline_status"]');
  if (status) {
    if (isDelivered(ctx)) {
      // The placeholder also sits in a mailto href, so the swap has to go through
      // innerHTML; stripping the reference keeps API data from becoming markup.
      const reference = (ctx.tracking?.reference || '').replace(/[^\w-]/g, '');
      status.innerHTML = status.innerHTML.replaceAll('#ORDER_NUMBER', reference);
      status.hidden = !status.textContent.trim();
    } else {
      const key = STEPS[current];
      const label = root.querySelector(`[data-step="${key}"] .order-tracking__timeline-step-label`)?.textContent.trim() || '';
      const detail = stepDetail(key, ctx, false);
      const message = detail ? `${label} (${detail})` : label;
      setText(status, message);
      status.hidden = !message;
    }
  }

  const ad = root.querySelector('[data-field="timeline_ad"]');
  if (ad && isDelivered(ctx)) setText(ad, ad.dataset.delivered);
};

const renderCards = ({ tracking, job, parcels = [], shopOrder = null }) => {
  const items = tracking?.items || [];
  const from = tracking?.from || {};
  const to = tracking?.to || {};
  const address = job?.address || to;

  // Matched to the warehouse items by SKU; without a line a row shows the name only.
  const paid = linesBySku(shopOrder);
  const paidLines = items.map((item) => takeLine(paid, item.sku));
  const specsRequests = paidLines.map((line) => getSpecs(line?.handle));

  const articles = el('div', 'order-tracking__items');
  articles.append(
    el('div', 'order-tracking__items-title', 'Artikelen'),
    ...items.map((item, i) => itemRow(item, paidLines[i])),
  );
  if (shopOrder?.totals) articles.append(totalsList(shopOrder.totals));

  const action = el('button', 'order-tracking__card-action btn btn-black-transparent', 'Wijziging doorgeven');
  action.type = 'button';
  action.addEventListener('click', () => window.$crisp?.push(['do', 'chat:open']));

  const outstanding = tracking?.outstanding_cod || 0;

  fillCard(
    'besteldetails',
    articles,
    metaList([
      ['Besteldatum', tracking?.order_date],
      ['E-mail', to.email],
      ['Telefoon', to.phone],
      ['Betaalmethode', outstanding > 0 ? 'Betalen aan de deur met PIN' : 'Al betaald'],
      ['Openstaand bedrag', money(outstanding)],
    ]),
    action,
  );

  const specsToggle = el('details', 'order-tracking__specs-toggle');
  specsToggle.open = true;
  const summary = el('summary', 'order-tracking__specs-summary', 'Gewicht en afmetingen');
  summary.append(chevron());
  const specsBody = el('div');
  specsToggle.append(summary, specsBody);
  specsBody.append(paragraph(['Wordt berekend…']));

  fillCard(
    'pakket',
    paragraph(addressLines(from, 'Mangohoutonline.nl'), 'Afzender'),
    paragraph([deliveryOption(tracking, shopOrder)], 'Bezorgoptie'),
    ...(job ? [traceBlock('Track & trace-code', [{ url: job.tracer_url, code: job.trace }])] : []),
    ...parcelBlocks(parcels),
    specsToggle,
  );

  // No package metafields, no row: that is what keeps service lines such as
  // "betaal bij levering" out, since Shopify marks them as shipped goods too.
  Promise.all(specsRequests).then((resolved) => {
    const shipped = items
      .map((item, i) => ({ item, line: paidLines[i], specs: resolved[i] }))
      .filter(({ specs }) => hasPackageData(specs));

    specsToggle.hidden = shipped.length === 0;
    if (specsToggle.hidden) return;

    specsBody.replaceChildren(
      shipped.length === 1
        ? paragraph(soloDetails(shipped[0].item, shipped[0].specs))
        : defList('specs', shipped.map(({ item, line, specs }) => [articleName(item, line), articleDetails(item, specs)])),
    );
  });

  fillCard('bezorgadres', paragraph(addressLines(address, job?.contact?.name || address.name)));
};

const render = (root, ctx) => {
  renderTitle(root, ctx);
  renderTimeline(root, ctx);
  renderCards(ctx);
};

/* ============================== Element ============================== */

const pushUrl = (order, email) => {
  const url = new URL(location.href);
  url.searchParams.set('order', order);
  url.searchParams.set('email', email);
  history.replaceState({}, '', url);
};

class OrderTracking extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-track-form]')?.addEventListener('submit', (event) => this.#onSubmit(event));

    // The section script parsed the deep link; without one we open on the form.
    const { order, email } = this.dataset;
    if (!order || !email) {
      this.#showScreen('form');
      return;
    }

    this.#fillInputs(order, email);
    this.#lookup(order, email);
  }

  #showScreen(name) {
    this.querySelector('[data-screen="form"]')?.toggleAttribute('hidden', name !== 'form');
    this.querySelector('[data-screen="result"]')?.toggleAttribute('hidden', name !== 'result');
  }

  #showError(message) {
    const error = this.querySelector('[data-form-error]');
    if (!error) return;
    error.textContent = message ?? '';
    error.hidden = !message;
  }

  #fillInputs(order, email) {
    const inputs = { order, email };
    for (const [name, value] of Object.entries(inputs)) {
      const input = this.querySelector(`[data-input="${name}"]`);
      if (input) input.value = value;
    }
  }

  #backToForm(message) {
    document.body.classList.remove('order-tracking-visible', 'order-tracking-loading');
    this.#showScreen('form');
    this.#showError(message);
  }

  #onSubmit(event) {
    event.preventDefault();
    const order = this.querySelector('[data-input="order"]')?.value.trim();
    const email = this.querySelector('[data-input="email"]')?.value.trim();
    if (!order || !email) return this.#showError(textOf(this, 'empty'));
    this.#lookup(order, email);
  }

  async #lookup(input, email) {
    const order = normalizeOrder(input);
    this.#showError('');
    document.body.classList.add('order-tracking-visible', 'order-tracking-loading');
    this.#showScreen('result');

    try {
      // Only the delivery lookup needs the zip from the transport record, so the
      // shop order runs alongside it instead of after it.
      const shopOrderRequest = getShopOrder(order, email);
      const tracking = await getOrder(order, email);
      if (!tracking) return this.#backToForm(textOf(this, 'notfound'));

      const [entries, shopOrder] = await Promise.all([
        getDeliveryEntries(order, tracking.to?.zip),
        shopOrderRequest,
      ]);
      const { job, parcels } = splitEntries(entries);
      render(this, { tracking, job, parcels, shopOrder });
      document.body.classList.remove('order-tracking-loading');
      pushUrl(order, email);
    } catch {
      this.#backToForm(textOf(this, 'error'));
    }
  }
}

if (!customElements.get('order-tracking')) customElements.define('order-tracking', OrderTracking);
